import polyglotI18nProvider from "ra-i18n-polyglot";
import {
  Admin,
  CustomRoutes,
  Resource,
  localStorageStore,
  useStore,
  StoreContextProvider,
} from "react-admin";
import { Route } from "react-router";
import { HowToVote } from "@mui/icons-material";

import authProvider from "./providers/authProvider";
import { Dashboard } from "./pages/dashboard";
import firebaseDataProvider from "./providers/dataProvider";
import englishMessages from "./i18n/en";
import { Layout, Login } from "./layout";
import Likes from "./resources/likes/LikeList";
import HandsUp from "./resources/handsUp/HandsUp";
import VotingEventsList from "./resources/voting/votingEventsList";
import { themes, ThemeName } from "./themes/themes";
import NotificationPage from "./pages/notifications/NotificationPage";
import CategoriesList from "./resources/categories/CategoriesList";
import CategoryEdit from "./resources/categories/CategoryEdit";
import CategoryCreate from "./resources/categories/CategoryCreate";
import { VotingEventCreate, VotingEventEdit } from "./resources/voting/votingEventForm";
import MessageTemplatesPage from "./pages/messageTemplates/MessageTemplatesPage";
import ShowsList from "./resources/shows/ShowsList";
import ShowCreate from "./resources/shows/ShowCreate";
import ShowEdit from "./resources/shows/ShowEdit";
import ChannelHealthPage from "./pages/channelHealth/ChannelHealthPage";
import ChannelsList from "./resources/channels/ChannelsList";
import ChannelCreate from "./resources/channels/ChannelCreate";
import ChannelEdit from "./resources/channels/ChannelEdit";
import AdminsList from "./resources/admins/AdminsList";
import AdminCreate from "./resources/admins/AdminCreate";
import AdminEdit from "./resources/admins/AdminEdit";
import LineupGamePage from "./pages/lineupGame/LineupGamePage";

const i18nProvider = polyglotI18nProvider(
  (locale) => {
    return englishMessages;
  },
  "en",
  [{ locale: "en", name: "English" }]
);

const store = localStorageStore(undefined, "ECommerce");

const App = () => {
  const [themeName] = useStore<ThemeName>("themeName", "soft");
  const lightTheme = themes.find((theme) => theme.name === themeName)?.light;
  const darkTheme = themes.find((theme) => theme.name === themeName)?.dark;
  return (
    <Admin
      title="Radio Admin"
      dataProvider={firebaseDataProvider}
      store={store}
      authProvider={authProvider}
      dashboard={Dashboard}
      loginPage={Login}
      layout={Layout}
      i18nProvider={i18nProvider}
      disableTelemetry
      lightTheme={lightTheme}
      darkTheme={darkTheme}
      defaultTheme="light"
    >
      <CustomRoutes>
        <Route path="/likes" element={<Likes />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path="/handsup" element={<HandsUp />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path="/notifications" element={<NotificationPage />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path="/messageTemplates" element={<MessageTemplatesPage />} />
      </CustomRoutes>
      <CustomRoutes>
        {/* Categories List */}
        <Route path="/categories" element={<CategoriesList />} />
        {/* Create Category Route with resource passed */}
        <Route path="/categories/create" element={<CategoryCreate />} />
        {/* Edit Category Route */}
        <Route path="/categories/:id/edit" element={<CategoryEdit />} />
      </CustomRoutes>
      <CustomRoutes>
        {/* Voting Events List */}
        <Route path="/votingEvents" element={<VotingEventsList />} />
        {/* Create Voting Event Route */}
        <Route path="/votingEvents/create" element={<VotingEventCreate />} />
        {/* Edit Voting Event Route */}
        <Route path="/votingEvents/:id/edit" element={<VotingEventEdit />} />
      </CustomRoutes>
      <CustomRoutes>
        {/* Shows List */}
        <Route path="/shows" element={<ShowsList />} />
        {/* Create Show Route */}
        <Route path="/shows/create" element={<ShowCreate />} />
        {/* Edit Show Route */}
        <Route path="/shows/:id/edit" element={<ShowEdit />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path="/channels" element={<ChannelsList />} />
        <Route path="/channels/create" element={<ChannelCreate />} />
        <Route path="/channels/:id/edit" element={<ChannelEdit />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path="/channelHealth" element={<ChannelHealthPage />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path="/lineupGame" element={<LineupGamePage />} />
      </CustomRoutes>
      <CustomRoutes>
        {/* Admins (Access Control) */}
        <Route path="/admins" element={<AdminsList />} />
        <Route path="/admins/create" element={<AdminCreate />} />
        <Route path="/admins/:id/edit" element={<AdminEdit />} />
      </CustomRoutes>
    </Admin>
  );
};

const AppWrapper = () => (
  <StoreContextProvider value={store}>
    <App />
  </StoreContextProvider>
);

export default AppWrapper;
